import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Star, ShoppingBag, Users, Download,
  Check, Loader2, ExternalLink, Crown
} from "lucide-react";
import api from "../lib/api";

export default function CreatorStorefrontPage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [showTiers, setShowTiers] = useState(false);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    setLoading(true);
    api.get(`/marketplace/creators/${username}`)
      .then((r) => setCreator(r.data))
      .catch(() => navigate("/creators"))
      .finally(() => setLoading(false));
  }, [username, navigate]);

  const handleSubscribe = async (tier = "free", price = 0) => {
    setSubscribing(true);
    try {
      await api.post(`/marketplace/creators/${username}/subscribe`, { tier, price });
      toast.success("Subscribed!");
      setCreator((c) => ({ ...c, is_subscribed: true, subscriber_count: c.subscriber_count + 1 }));
      setShowTiers(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to subscribe");
    } finally {
      setSubscribing(false);
    }
  };

  const sortedItems = creator?.items?.slice().sort((a, b) => {
    if (sort === "popular") return (b.downloads_count || 0) - (a.downloads_count || 0);
    if (sort === "price") return (a.price || 0) - (b.price || 0);
    return 0; // newest = default order
  }) || [];

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;
  if (!creator) return null;

  return (
    <div className="min-h-screen" data-testid="creator-storefront-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/creators")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer" data-testid="storefront-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <span className="font-outfit text-sm font-semibold text-foreground">{creator.display_name}</span>
      </div>

      {/* Cover / Hero */}
      <div className="h-48 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 relative" data-testid="creator-cover">
        <div className="absolute inset-0 bg-black/10" />
      </div>

      {/* Creator Info */}
      <div className="max-w-4xl mx-auto px-4 -mt-16 relative z-10">
        <div className="flex items-end gap-4 mb-6">
          <div className="neon-ring shadow-glow-sm rounded-full">
            <UserAvatar name={creator.display_name} username={creator.username} avatarUrl={creator.avatar_url} size="xl" />
          </div>
          <div className="flex-1 pb-2">
            <h1 className="font-outfit text-xl font-semibold text-foreground" data-testid="creator-name">{creator.display_name}</h1>
            <p className="text-sm text-muted-foreground">@{creator.username}</p>
          </div>
          <div className="pb-2">
            {creator.is_subscribed ? (
              <Button variant="outline" className="border-border text-muted-foreground bg-card h-9 text-xs px-4 rounded-full" data-testid="subscribed-badge">
                <Check className="w-3.5 h-3.5 mr-1" /> Subscribed
              </Button>
            ) : (
              <Button
                onClick={() => creator.subscription_tiers?.length > 0 ? setShowTiers(true) : handleSubscribe()}
                disabled={subscribing}
                className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-9 text-xs px-5 rounded-full press-down"
                data-testid="subscribe-btn"
              >
                {subscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Crown className="w-3.5 h-3.5 mr-1" /> Subscribe</>}
              </Button>
            )}
          </div>
        </div>

        {/* Bio */}
        {(creator.creator_bio || creator.bio) && (
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{creator.creator_bio || creator.bio}</p>
        )}

        {/* Stats */}
        <div className="flex gap-6 mb-6 flex-wrap" data-testid="creator-stats">
          <div className="text-center">
            <span className="text-lg font-semibold text-foreground">{creator.items?.length || 0}</span>
            <span className="text-xs text-muted-foreground ml-1">Items</span>
          </div>
          <div className="text-center">
            <span className="text-lg font-semibold text-foreground">{creator.subscriber_count}</span>
            <span className="text-xs text-muted-foreground ml-1">Subscribers</span>
          </div>
          <div className="text-center">
            <span className="text-lg font-semibold text-foreground">{creator.total_sales}</span>
            <span className="text-xs text-muted-foreground ml-1">Sales</span>
          </div>
          {creator.avg_rating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-lg font-semibold text-foreground">{creator.avg_rating}</span>
              <span className="text-xs text-muted-foreground">avg</span>
            </div>
          )}
        </div>

        <div className="gradient-divider mb-6" />

        {/* Profile Link */}
        <Link to={`/profile/${creator.username}`} className="inline-flex items-center gap-1 text-xs text-cyan-400 no-underline hover:text-cyan-300 mb-6" data-testid="view-social-profile">
          <ExternalLink className="w-3 h-3" /> View social profile
        </Link>

        {/* Sort + Listings */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-foreground">Listings ({sortedItems.length})</h2>
          <div className="flex gap-1">
            {[{id:"newest",label:"Newest"},{id:"popular",label:"Popular"},{id:"price",label:"Price"}].map((s) => (
              <button key={s.id} onClick={() => setSort(s.id)} className={`px-2.5 py-1 rounded text-[11px] font-medium border-0 cursor-pointer ${sort === s.id ? "bg-cyan-500/10 text-cyan-400" : "bg-transparent text-muted-foreground/70 hover:text-muted-foreground"}`} data-testid={`sort-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {sortedItems.length === 0 ? (
          <div className="text-center py-16"><ShoppingBag className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No listings yet</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-12" data-testid="creator-listings">
            {sortedItems.map((item) => (
              <Link to={`/marketplace/${item.id}`} key={item.id} className={`bg-card border border-border/60 rounded-xl overflow-hidden no-underline card-hover group cat-accent-${item.category || "productivity"}`} data-testid={`creator-item-${item.id}`}>
                <div className="h-28 bg-gradient-to-br from-cyan-500/10 to-violet-500/10 flex items-center justify-center">
                  <ShoppingBag className="w-8 h-8 text-cyan-300 group-hover:text-cyan-400 transition-colors" />
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="text-sm font-semibold text-foreground line-clamp-1 flex-1">{item.title}</h3>
                    <span className={`text-xs font-semibold ml-2 px-2 py-0.5 rounded-full ${item.is_free ? "bg-emerald-500/10 text-emerald-400" : "bg-cyan-500/10 text-cyan-400"}`}>
                      {item.is_free ? "Free" : `$${item.price?.toFixed(2)}`}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{item.description}</p>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70">
                    {item.rating_count > 0 && <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" /> {item.rating_avg}</span>}
                    <span className="flex items-center gap-0.5"><Download className="w-3 h-3" /> {item.downloads_count}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Subscription Tiers Modal */}
      {showTiers && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowTiers(false)} data-testid="tiers-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-outfit text-lg font-semibold text-foreground mb-4">Subscribe to {creator.display_name}</h2>
            <div className="space-y-3">
              <button onClick={() => handleSubscribe("free", 0)} className="w-full p-4 border border-border rounded-xl text-left cursor-pointer bg-card hover:bg-white/5 transition-colors" data-testid="tier-free">
                <p className="text-sm font-semibold text-foreground">Free</p>
                <p className="text-xs text-muted-foreground">Get notified about new listings</p>
              </button>
              {(creator.subscription_tiers || []).map((tier, i) => (
                <button key={i} onClick={() => handleSubscribe(tier.name || "supporter", tier.price || 5)} className="w-full p-4 border border-cyan-500/30 rounded-xl text-left cursor-pointer bg-cyan-500/10 hover:bg-cyan-500/15 transition-colors" data-testid={`tier-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground">{tier.name || "Supporter"}</p>
                    <span className="text-sm font-bold text-cyan-400">${tier.price || 5}/mo</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{tier.benefits || "Exclusive content and early access"}</p>
                </button>
              ))}
            </div>
            <button onClick={() => setShowTiers(false)} className="w-full mt-3 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer hover:text-foreground/80">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
